import React, { useMemo } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuthContext } from '@/lib/auth-context'
import { useProspectLockerRoom } from '@/hooks/useProspectLockerRoom'
import ErrorBoundary from '@/components/ErrorBoundary'

// Coach navigation items - full management access
const COACH_NAV_ITEMS = [
  { label: 'Home', to: '/portal/locker' },
  { label: 'Roster', to: '/portal/locker/roster' },
  { label: 'Budget', to: '/portal/locker/budget' },
  { label: 'Shopping', to: '/portal/locker/shopping' },
  { label: 'Marketplace', to: '/portal/locker/marketplace' },
  { label: 'Leaderboards', to: '/portal/locker/leaderboard' },
  { label: 'AI Tools', to: '/portal/locker/tools' },
  { label: 'AI Marketplace', to: '/portal/locker/ai-marketplace' },
  { label: 'Review', to: '/portal/coach/review' },
  { label: 'Highlight App', to: '/portal/locker/app' },
  { label: 'The Combine', to: '/portal/prospect/combine' },
  { label: 'My Nominees', to: '/portal/locker/nominees' },
  { label: 'Public Page', to: '/portal/locker/public' },
]

// Prospect navigation items - personal view with restrictions
const PROSPECT_NAV_ITEMS = [
  { label: 'My Locker', to: '/portal/locker' },
  { label: 'My Profile', to: '/portal/prospect/profile' },
  { label: 'My Budget', to: '/portal/prospect/budget' },
  { label: 'Shop', to: '/portal/prospect/marketplace' },
  { label: 'Leaderboards', to: '/portal/locker/leaderboard' },
  { label: 'AI Tools', to: '/portal/prospect/tools' },
  { label: 'AI Marketplace', to: '/portal/prospect/ai-marketplace' },
  { label: 'Daily Routine', to: '/portal/prospect/routine' },
  { label: 'Media', to: '/portal/prospect/updates' },
  { label: 'The Combine', to: '/portal/prospect/combine' },
]

export default function LockerRoomLayout() {
  const { profile } = useAuthContext() || {}
  const { credits, lockerRoom } = useProspectLockerRoom()

  const role = profile?.role
  const isCoach = role === 'coach'
  const isAdmin = role === 'admin' || role === 'superadmin'

  const title = useMemo(() => {
    if (isCoach) return 'Locker Room (Coach)'
    if (isAdmin) return 'Locker Room (Admin)'
    return lockerRoom?.public_name || lockerRoom?.name || 'My Locker Room'
  }, [isCoach, isAdmin, lockerRoom?.public_name, lockerRoom?.name])

  const subtitle = useMemo(() => {
    if (isCoach) return 'Manage your athletes, budgets, and team resources from one dashboard.'
    if (isAdmin) return 'Administrative view of locker room operations.'
    return 'Your personal space for training, shopping, and growth. Your coach manages the team budget.'
  }, [isCoach, isAdmin])

  const navItems = isCoach || isAdmin ? COACH_NAV_ITEMS : PROSPECT_NAV_ITEMS
  const creditBalance = credits?.balance_credits ?? 0

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-stone-900">{title}</h1>
            {!isCoach && !isAdmin && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                Athlete
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-stone-600">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-gradient-to-r from-cocoa to-cocoa/80 px-4 py-2 text-white shadow">
            <p className="text-xs text-white/70">Credits</p>
            <p className="text-lg font-bold">{creditBalance.toLocaleString()}</p>
          </div>
          {isCoach && (
            <div className="rounded-xl border border-stone-200 bg-white px-4 py-2 shadow-sm">
              <p className="text-xs text-stone-500">View Mode</p>
              <p className="text-sm font-semibold text-stone-900">Coach</p>
            </div>
          )}
        </div>
      </header>

      <nav className="mb-6 flex flex-wrap gap-2 border-b border-stone-200 pb-4">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            end={item.to === '/portal/locker' || item.to === '/portal/prospect'}
            to={item.to}
            className={({ isActive }) =>
              `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-cocoa text-white shadow'
                  : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <ErrorBoundary fallbackPath="/portal/locker" fallbackLabel="Back to Locker Room">
        <Outlet />
      </ErrorBoundary>
    </div>
  )
}
